import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Input from '../../../../../components/Input';
import Switch from '../../../../../components/Switch';
import Button from '../../../../../components/Button';
import Toast from '../../../../../components/Toast';
import StockistProductService from '../../../../../services/stockist.product.service';
import useFetch from '../../../../../hooks/useFetch';
import { APP_SUCCESS_MESSAGES, VALIDATION_FIELD_MAPPING } from '../../../../../utils/appConstant';
import { toast } from 'react-toastify';
import { validateProductForm } from '../validations';
import { StockistProductAddEditApi } from '../../../../../interfaces/stockist.interface';

interface ProductAddEditProps {
  id?: number | null;
  stockistId: number;
  onClose: () => void;
  onSuccess: () => void;
}

const initialFormData = {
  [VALIDATION_FIELD_MAPPING.name]: '',
  [VALIDATION_FIELD_MAPPING.description]: '',
  [VALIDATION_FIELD_MAPPING.status]: true,
};

const ProductAddEdit: React.FC<ProductAddEditProps> = ({ id, stockistId, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState<any>({ ...initialFormData });
  const [loading, setLoading] = useState(false);
  const [validation, setValidation] = useState<any>({ validate: true, fields: [] });

  useFetch({
    service: StockistProductService,
    func: 'get',
    payload: id,
    dependency: [id],
    skip: !id,
    onSuccess: (data: any) => {
      setFormData({
        [VALIDATION_FIELD_MAPPING.name]: data.name,
        [VALIDATION_FIELD_MAPPING.description]: data.description,
        [VALIDATION_FIELD_MAPPING.status]: data.is_active,
      });
    },
  });

  const handleInputChange = useCallback(
    (event: any, name: string) => {
      let value: any;
      if (name === VALIDATION_FIELD_MAPPING.status) {
        value = event.target.checked;
      } else {
        value = event.target.value;
      }

      setFormData((prev: any) => ({
        ...prev,
        [name]: value,
      }));
    },
    []
  );

  const handleInputBlur = useCallback(
    (e: any, name: string) => {
      const newValidation = validateProductForm(
        { ...formData, [name]: e.target.value.trim() },
        name
      );
      setValidation(newValidation);
    },
    [formData]
  );

  const handleSubmit = async () => {
    const validations = validateProductForm(formData);
    setValidation(validations);
    if (!validations.validate) return;

    setLoading(true);
    try {
      const payload: StockistProductAddEditApi = {
        name: formData[VALIDATION_FIELD_MAPPING.name].trim(),
        description: formData[VALIDATION_FIELD_MAPPING.description].trim(),
        is_active: formData[VALIDATION_FIELD_MAPPING.status],
        stockist_id: stockistId,
      };

      if (id) {
        await StockistProductService.edit(String(id), payload);
        toast.success(<Toast message={APP_SUCCESS_MESSAGES.stockistProductUpdated} />, { icon: false });
      } else {
        await StockistProductService.add(payload);
        toast.success(<Toast message={APP_SUCCESS_MESSAGES.stockistProductCreated} />, { icon: false });
      }
      onSuccess();
    } catch (error) {
      // Error handled by http interceptor
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (field: string) => {
    if (validation.validate && validation.fields.length === 0) return undefined;
    return validation.fields.find((f: any) => f.field === field)?.message;
  };

  return (
    <div className="d-flex flex-column h-100">
      <div className="row g-6">
        <h6 className="mb-0">1. {t('stockists.stockistProduct.addEdit.pageTexts.form.sections.primaryDetails')}</h6>

        <div className="col-12">
          <div className="form-floating form-floating-outline">
            <Input
              required
              label={t('stockists.stockistProduct.addEdit.pageTexts.form.name.label')}
              type="text"
              id={VALIDATION_FIELD_MAPPING.name}
              name={VALIDATION_FIELD_MAPPING.name}
              value={formData[VALIDATION_FIELD_MAPPING.name]}
              placeholder={t('stockists.stockistProduct.addEdit.pageTexts.form.name.placeholder')}
              onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.name)}
              onBlur={(e) => handleInputBlur(e, VALIDATION_FIELD_MAPPING.name)}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.name)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.name)}
            />
          </div>
        </div>

        <div className="col-12">
          <div className="form-floating form-floating-outline">
            <Input
              required
              label={t('stockists.stockistProduct.addEdit.pageTexts.form.description.label')}
              type="text"
              id={VALIDATION_FIELD_MAPPING.description}
              name={VALIDATION_FIELD_MAPPING.description}
              value={formData[VALIDATION_FIELD_MAPPING.description]}
              placeholder={t('stockists.stockistProduct.addEdit.pageTexts.form.description.placeholder')}
              onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.description)}
              onBlur={(e) => handleInputBlur(e, VALIDATION_FIELD_MAPPING.description)}
              error={!!getErrorMessage(VALIDATION_FIELD_MAPPING.description)}
              errorMessage={getErrorMessage(VALIDATION_FIELD_MAPPING.description)}
            />
          </div>
        </div>
      </div>

      <hr className="my-6 mx-n4" />

      <div className="row g-6">
        <h6 className="mb-0">2. {t('stockists.stockistProduct.addEdit.pageTexts.form.sections.otherInformation')}</h6>

        <div className="col-12">
          <div className="form-floating form-floating-outline">
            <Switch
              label={
                formData[VALIDATION_FIELD_MAPPING.status]
                  ? t('stockists.stockistProduct.addEdit.pageTexts.form.status.activeLabel')
                  : t('stockists.stockistProduct.addEdit.pageTexts.form.status.inActiveLabel')
              }
              checked={formData[VALIDATION_FIELD_MAPPING.status]}
              name={VALIDATION_FIELD_MAPPING.status}
              onChange={(e) => handleInputChange(e, VALIDATION_FIELD_MAPPING.status)}
            />
          </div>
        </div>
      </div>

      <div className="mt-auto pt-4 d-flex justify-content-end gap-3">
        <Button
          className="btn btn-outline-secondary"
          text={t('stockists.addEdit.pageTexts.form.buttons.cancel')}
          onClick={onClose}
          disabled={loading}
        />
        <Button
          className="btn btn-primary"
          text={t('stockists.addEdit.pageTexts.form.buttons.save')}
          onClick={handleSubmit}
          loading={loading}
          disabled={loading}
        />
      </div>
    </div>
  );
};

export default ProductAddEdit;
